import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { User } from '@supabase/supabase-js';
import ProfileHeader from './components/ProfileHeader';
import PersonalInfoSection from './components/PersonalInfoSection';
import PasswordSection from './components/PasswordSection';
import NotificationsSection from './components/NotificationsSection';
import DangerZoneSection from './components/DangerZoneSection';

type TabId = 'personal' | 'password' | 'notifications' | 'account';

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: 'personal', label: 'Personal Info', icon: 'ri-user-3-line' },
  { id: 'password', label: 'Security', icon: 'ri-lock-line' },
  { id: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line' },
  { id: 'account', label: 'Account', icon: 'ri-shield-line' },
];

export default function ProfilePage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('personal');
  const navigate = useNavigate();

  useEffect(() => {
    // Helper to redirect unauthenticated users
    const redirectUnauth = () => {
      navigate('/auth', { state: { from: { pathname: '/profile' } } });
    };

    // Initial session check
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!session?.user) {
          redirectUnauth();
          return;
        }
        setUser(session.user);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching session:', error);
        // Keep loading indicator but show a user‑friendly message
        setLoading(false);
        redirectUnauth();
      });

    // Real‑time auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        redirectUnauth();
        return;
      }
      setUser(session.user);
    });

    // Cleanup listener on unmount
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Show a spinner while we're loading or if the user isn't set yet
  if (loading || !user) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#f04e58]/20 border-t-[#f04e58] rounded-full animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ProfileHeader userName={user.user_metadata?.full_name || ''} userEmail={user.email || ''} />

      <section className="py-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Sidebar Tabs */}
            <div className="md:w-56 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 p-2 md:sticky md:top-24">
                <nav className="flex md:flex-col gap-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap w-full text-left ${
                        activeTab === tab.id
                          ? 'bg-[#fef5f5] text-[#f04e58]'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${tab.icon} text-base`}></i>
                      </div>
                      <span className="hidden md:inline">{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {activeTab === 'personal' && <PersonalInfoSection user={user} onUserUpdate={setUser} />}
              {activeTab === 'password' && <PasswordSection />}
              {activeTab === 'notifications' && <NotificationsSection />}
              {activeTab === 'account' && <DangerZoneSection />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
